import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';

import { Store } from '@ngrx/store';
import { combineLatest, Observable, Subscription } from 'rxjs';
import { filter, map, take } from 'rxjs/operators';

import { CartStatus } from '@fcom/dapi';
import { snapshot } from '@fcom/rx';
import {
  Category,
  ControlData,
  FinnairCart,
  FinnairItineraryItemFlight,
  FinnairPassengerCode,
  FinnairServiceRequestItem,
} from '@fcom/dapi/api/models';
import { ToasterService } from '@fcom/common/services';
import {
  getAllFlightsFromCartOrOrder,
  shouldShowKidsInBusinessNotification,
} from '@fcom/common-booking/utils/common-booking.utils';
import { AncillaryToasterMessages, getServiceData } from '@fcom/common-booking/utils/ancillaries.utils';
import { unsubscribe } from '@fcom/core/utils';
import { ModalButtons, NotificationTheme } from '@fcom/ui-components';
import { ElementActions, ElementTypes } from '@fcom/common/interfaces';
import { GtmService } from '@fcom/common/gtm';
import { BOOKING_STEPS } from '@fcom/common/config/booking-config';
import { BookingAppState, PaxDetailsState } from '@fcom/common/interfaces/booking';
import { setNoAvailability } from '@fcom/common-booking/store/actions/seat-map.actions';
import { SeatMapActions } from '@fcom/common-booking/store';
import { ConfigService } from '@fcom/core/services';
import { profileType } from '@fcom/core/selectors';
import { ProfileType } from '@fcom/core-api/login';

import { CartActions } from '../../../store/actions';
import { cartData, cartStatus, isCartDirty, paxDetails } from '../../../store/selectors';
import { BookingQueueService, BookingService } from '../../../services';
import { CountryCode } from '../../../utils';
import { KoreaNotificationComponent } from '../components/korea-notification.component';
import { ENABLED_PAX_ANCILLARY_CATEGORIES } from '../constants';
import { KidsInBusinessNotificationComponent } from '../components/kids-in-business-notification/kids-in-business-notification.component';

@Component({
  selector: 'fin-booking-pax-details-route',
  templateUrl: './booking-pax-details-route.component.html',
  standalone: false,
})
export class BookingPaxDetailsRouteComponent implements OnInit, OnDestroy {
  readonly cartData$: Observable<FinnairCart>;
  readonly cartStatus$: Observable<CartStatus>;
  readonly showKoreaNotification$: Observable<boolean>;
  readonly ModalButtons = ModalButtons;
  readonly NotificationTheme = NotificationTheme;

  subscription = new Subscription();
  continueOrSkip: 'skip' | 'continue' | null = null;

  @ViewChild('koreaNotification', { static: false })
  private koreaNotification: KoreaNotificationComponent;

  @ViewChild('kidsInBusinessNotification', { static: false })
  private kidsInBusinessNotification: KidsInBusinessNotificationComponent;

  constructor(
    private bookingService: BookingService,
    private store$: Store<BookingAppState>,
    private bookingQueueService: BookingQueueService,
    private toasterService: ToasterService,
    private gtmService: GtmService,
    private configService: ConfigService
  ) {
    this.cartData$ = this.store$.pipe(cartData());
    this.cartStatus$ = this.store$.pipe(cartStatus());
    this.showKoreaNotification$ = this.cartData$.pipe(
      map((cd) => cd.controlData),
      filter(Boolean),
      map(({ originCountryCode }: ControlData) => originCountryCode === CountryCode.KOREA)
    );
  }

  ngOnInit(): void {
    this.subscription.add(
      this.cartData$.pipe(filter(Boolean), take(1)).subscribe((cart) => {
        if (cart.otherInformation?.useSeatmapPrefetch) {
          this.prefetchAllSeatmaps();
        }
      })
    );
  }

  ngOnDestroy(): void {
    unsubscribe(this.subscription);
  }

  openModalOrConfirm(event: { skipToCheckout: boolean }): void {
    this.continueOrSkip = event.skipToCheckout ? 'skip' : 'continue';

    this.confirmOrDisplayKoreanNotification();
  }

  confirmOrDisplayKoreanNotification(): void {
    this.subscription.add(
      this.showKoreaNotification$.pipe(take(1)).subscribe((show) => {
        if (show) {
          this.koreaNotification.openModal();
        } else {
          this.confirmOrDisplayKidsInBusinessNotification();
        }
      })
    );
  }

  confirmOrDisplayKidsInBusinessNotification(): void {
    this.subscription.add(
      this.cartData$.pipe(take(1)).subscribe((cart) => {
        const passengerFormDetails: PaxDetailsState = snapshot(this.store$.pipe(paxDetails()));

        if (
          this.configService.cfg.enableKidsInBusinessNotification &&
          shouldShowKidsInBusinessNotification(cart.bounds, passengerFormDetails.passengers)
        ) {
          this.kidsInBusinessNotification.openModal();
        } else {
          this.confirmPaxDetails();
        }
      })
    );
  }

  confirmPaxDetails(): void {
    const passengerFormDetails: PaxDetailsState = snapshot(this.store$.pipe(paxDetails()));
    this.updateCartWithPaxAncillaries(ENABLED_PAX_ANCILLARY_CATEGORIES);
    const cd = snapshot(this.cartData$);
    if (cd.otherInformation?.useSeatmapPrefetch && this.anyPassengerHasChangesToFFDetails(passengerFormDetails, cd)) {
      this.store$.dispatch(SeatMapActions.reset());
    }
    this.bookingQueueService.queuePassengersAndCorporateCodeUpdate(passengerFormDetails);

    if (this.continueOrSkip === 'skip') {
      this.store$.dispatch(CartActions.acceptTerms());
      this.bookingService.setStep({ step: BOOKING_STEPS.CHECKOUT });
      this.gtmService.trackElement(
        'go-to-payment',
        'booking-flow_shortcut',
        ElementTypes.BUTTON,
        '',
        ElementActions.CLICK
      );
    } else {
      this.bookingService.nextStep();
    }

    this.continueOrSkip = null;
  }

  updateCartWithPaxAncillaries(categories: Category[]): void {
    if (snapshot(this.store$.pipe(isCartDirty()))) {
      this.subscription.add(
        this.cartData$.pipe(take(1)).subscribe((currentCartData) => {
          const flights: FinnairItineraryItemFlight[] = getAllFlightsFromCartOrOrder(currentCartData);

          const ancillaryRequestUpdates = categories.map((category) =>
            getServiceData(this.store$, category, currentCartData.bounds, flights)
          );

          const allRequests: { requests: FinnairServiceRequestItem[]; messages: AncillaryToasterMessages[] } =
            ancillaryRequestUpdates.reduce(
              (current, [requests, messages]) => {
                return { requests: current.requests.concat(requests), messages: current.messages.concat(messages) };
              },
              { requests: [], messages: [] }
            );

          this.bookingQueueService.queueServicesUpdate(allRequests.requests, {
            error: () => {
              allRequests.messages.forEach((msg) => {
                this.toasterService.addMessageToQueue(msg.error);
              });
            },
          });
        })
      );
    }
  }

  resetSeatMap(): void {
    this.bookingQueueService.queueSeatSelectionReset();
  }

  goBackToFlightSelection(): void {
    this.bookingService.prevStep();
  }

  private prefetchAllSeatmaps(): void {
    this.subscription.add(
      combineLatest([this.cartData$, this.cartStatus$, this.store$.pipe(profileType())])
        .pipe(
          filter(([cart, status]) => !!cart && status === CartStatus.OK),
          filter(([_cart, _status, userProfileType]) => userProfileType !== ProfileType.CORPORATE),
          filter(
            ([cart]) => !cart.passengers?.some((passenger) => passenger.passengerTypeCode === FinnairPassengerCode.INF)
          ),
          take(1)
        )
        .subscribe(([cart]) => {
          const flights = getAllFlightsFromCartOrOrder(cart);
          const currencyCode = cart.prices?.unpaid?.total?.total?.totalAmount?.currencyCode;
          flights.forEach((flight) => this.fetchSeatMapOrSetNoAvailability(flight, cart, currencyCode));
        })
    );
  }

  private fetchSeatMapOrSetNoAvailability(
    flight: FinnairItineraryItemFlight,
    cart: FinnairCart,
    currencyCode: string
  ): void {
    const hasAvailability = Object.values(
      cart.eligibilities?.serviceCatalog?.categories.find((c) => c.category === Category.SEAT)?.services?.[flight.id] ??
        {}
    ).some(({ isAllowedToUse }) => isAllowedToUse);
    const hasControlAvailability = cart.controlData.sellAncillariesPerFlight[flight.flightNumber] || false;

    if (!hasAvailability && !hasControlAvailability) {
      this.store$.dispatch(setNoAvailability({ flightId: flight.id }));
      return;
    }

    this.bookingQueueService.queueTriggerFetchSeatMap(flight.id, currencyCode);
  }

  private anyPassengerHasChangesToFFDetails(passengerFormDetails: PaxDetailsState, finnairCart: FinnairCart): boolean {
    return passengerFormDetails.passengers.some(
      (pax) =>
        pax.frequentFlyerCard?.cardNumber &&
        pax.frequentFlyerCard?.companyCode &&
        !finnairCart.passengers
          .find((p) => p.id === pax.travelerId)
          ?.frequentFlyerCards?.some(
            (ffCard) =>
              ffCard.cardNumber === pax.frequentFlyerCard.cardNumber &&
              ffCard.companyCode === pax.frequentFlyerCard.companyCode
          )
    );
  }
}
